import * as React from 'react';
import {TextField, Select, Button, Checkbox, Divider, CircularProgress, Switch, FormControlLabel} from '@mui/material';
import {Company, Option, Person, Subject} from '../../../../store/common-values/commonTypes';
import {Autocomplete} from '@mui/material';
import {Project} from '../../../../store/projects';
import ReactQuill from "react-quill"
import 'date-fns';
import dayjs from 'dayjs';
import AsyncPicker from '../../../application/AsyncPicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import SaveIcon from '@mui/icons-material/Save';
import Stack from "@mui/material/Stack";

const requiredFields: ((p: Project) => any)[] = [
    p => p.name,
    p => p.address,
    p => p.zip,
    p => p.city,
    p => p.regionId,
    p => p.buildingTypeId,
    p => p.auditScopeId,
    p => p.projectTypeId,
    p => p.breeamVersionId,
    p => p.furnishedStatusId,
    p => p.sizeTypeId,
    p => p.realSize,
    p => p.customer?.name,
    p => p.customer?.orgNumber,
    p => p.contactPerson?.email,
    p => p.contactPerson?.fullName,
    p => p.invoiceEmail,
    p => p.invoiceNumber
];

export interface ProjectNewFormProps {
    onSave: (project: Project) => void;
    onCancel: () => void;
    getCompaniesByName: (name: string) => Promise<Company[]>;
    getCompaniesByNumber: (number: string) => Promise<Company[]>;
    getPersonsByName: (name: string) => Promise<Person[]>;
    getPersonsByEmail: (email: string) => Promise<Person[]>;
    breeamVersions: Option[];
    buildingTypes: Option[];
    auditPhases: Option[];
    projectTypes: Option[];
    sizeTypes: Option[];
    furnishedStatuses: Option[];
    aps: Person[];
    regions: Option[];
    isLoading: boolean;
}

export interface ProjectNewFormState {
    project: Project;
    breeamVersions: Option[];
    buildingTypes: Option[];
    projectTypes: Option[];
    sizeTypes: Option[];
    auditPhases: Option[];
    furnishedStatuses: Option[];
    regions: Option[];
    aps: Person[];
    isLoading: boolean;
}

export default class ProjectNewForm extends React.Component<ProjectNewFormProps, ProjectNewFormState> {
    constructor(props: ProjectNewFormProps) {
        super(props);

        this.state = {
            project: {
                customer: {} as Company,
                contactPerson: {} as Person,
                auditorCompany: null,
                estimatedQaCompletionDate: null,
                estimatedQaEngineeringDate: null
            } as Project,
            breeamVersions: props.breeamVersions,
            buildingTypes: props.buildingTypes,
            projectTypes: props.projectTypes,
            sizeTypes: props.sizeTypes,
            furnishedStatuses: props.furnishedStatuses,
            auditPhases: props.auditPhases,
            aps: props.aps,
            regions: props.regions,
            isLoading: props.isLoading
        };
    }

    componentDidUpdate(prevProps: ProjectNewFormProps) {
        if (this.props.breeamVersions?.length !== prevProps.breeamVersions?.length) {
            this.setState({breeamVersions: this.props.breeamVersions || []});
        }
        if (this.props.buildingTypes?.length !== prevProps.buildingTypes?.length) {
            this.setState({buildingTypes: this.props.buildingTypes || []});
        }
        if (this.props.sizeTypes?.length !== prevProps.sizeTypes?.length) {
            this.setState({sizeTypes: this.props.sizeTypes || []});
        }
        if (this.props.furnishedStatuses?.length !== prevProps.furnishedStatuses?.length) {
            this.setState({furnishedStatuses: this.props.furnishedStatuses || []});
        }
        if (this.props.projectTypes?.length !== prevProps.projectTypes?.length) {
            this.setState({projectTypes: this.props.projectTypes || []});
        }
        if (this.props.auditPhases?.length !== prevProps.auditPhases?.length) {
            this.setState({auditPhases: this.props.auditPhases || []});
        }
        if (this.props.aps?.length !== prevProps.aps?.length) {
            this.setState({aps: this.props.aps || []});
        }
        if (this.props.regions?.length !== prevProps.regions?.length) {
            this.setState({regions: this.props.regions || []});
        }
        if (this.props.isLoading !== prevProps.isLoading) {
            this.setState({isLoading: this.props.isLoading});
        }
    }

    private isValid(): boolean {
        if (requiredFields.filter(f => !f(this.state.project)).length > 0) return false;

        return true;
    }

    private updateState(action: (s: ProjectNewFormState) => void): void {
        this.setState(s => {
            action(s);
            return s;
        });
    }

    private setField(event: any, setValue: (s: ProjectNewFormState, value: any) => void) {
        const {target} = event;
        this.updateState(s => setValue(s, target?.value));
    }

    private setPartOfLookupField<T>(option: T | null | string, partOfProperty: string, propertySelector: (s: ProjectNewFormState) => T | null, propertyUpdater: (s: ProjectNewFormState, value: T | null) => void): void {
        let newValue: any = {...propertySelector(this.state)};
        if (option != null && typeof option == "object")
            newValue = option;
        else {
            newValue = newValue != null ? {...newValue, id: undefined} : {} as T;
            newValue[partOfProperty] = option;
        }

        this.updateState(s => propertyUpdater(s, newValue));
    }

    public render() {
        return (
            <React.Fragment>
                <h4>Prosjektinfo</h4>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Prosjektets navn</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg navn"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.name}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.name = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Revisjonsfase</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.auditPhases}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            onChange={(e, t) => this.updateState(s => s.project.auditScopeId = t?.id)}
                            getOptionLabel={(option: Option) => option.label}
                            renderInput={(params) => <TextField {...params} placeholder="Valg revisjonsfase"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Prosjekttype</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.projectTypes}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            onChange={(e, t) => this.updateState(s => s.project.projectTypeId = t?.id)}
                            getOptionLabel={(option: Option) => option.label}
                            renderInput={(params) => <TextField {...params} placeholder="Valg prosjekttype"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>BREEAM-versjon</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.breeamVersions}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            onChange={(e, t) => this.updateState(s => s.project.breeamVersionId = t?.id)}
                            getOptionLabel={(option: Option) => option.label}
                            renderInput={(params) => <TextField {...params} placeholder="Valg versjon"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>AP</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.aps}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            onChange={(e, ap) => this.updateState(s => s.project.apId = ap?.id)}
                            getOptionLabel={(option: Person) => option.fullName}
                            renderInput={(params) => <TextField {...params} placeholder="Valg ap"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-6">
                        <Stack gap={1}>
                            <span>Estimert innsendingsdato for prosjekteringsfasesertifisering</span>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    variant="inline"
                                    format="DD.MM.YYYY"
                                    fullWidth
                                    disabled={this.state.isLoading}
                                    value={this.state.project.estimatedQaEngineeringDate ? dayjs(this.state.project.estimatedQaEngineeringDate) : null}
                                    renderInput={(params) => <TextField {...params} />}
                                    onChange={(date) => this.updateState(s => s.project.estimatedQaEngineeringDate = date ? date.toDate() : null)}

                                />
                            </LocalizationProvider>
                        </Stack>
                    </div>

                    <div className="col-sm-6">
                        <Stack gap={1}>
                            <span>Estimert innsendingsdato for ferdigstillelsesfase-sertifisering</span>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    variant="inline"
                                    format="DD.MM.YYYY"
                                    fullWidth
                                    disabled={this.state.isLoading}
                                    value={this.state.project.estimatedQaCompletionDate ? dayjs(this.state.project.estimatedQaCompletionDate) : null}
                                    renderInput={(params) => <TextField {...params} />}
                                    onChange={(date) => this.updateState(s => s.project.estimatedQaCompletionDate = date ? date.toDate() : null)}

                                />
                            </LocalizationProvider>
                        </Stack>
                    </div>
                </div>
                <h4>Bygginfo</h4>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Byggtype</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.buildingTypes}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            onChange={(e, t) => this.updateState(s => s.project.buildingTypeId = t?.id)}
                            getOptionLabel={(option: Option) => option.label}
                            renderInput={(params) => <TextField {...params} placeholder="Valg byggtype"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Innredet/uinnredet bygg</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.furnishedStatuses}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            onChange={(e, t) => this.updateState(s => s.project.furnishedStatusId = t?.id)}
                            getOptionLabel={(option: Option) => option.label}
                            renderInput={(params) => <TextField {...params}
                                                                placeholder="Valg innredet/uinnredet bygg"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Størrelseskategori på prosjekt (BRA)</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.sizeTypes}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            onChange={(e, t) => this.updateState(s => s.project.sizeTypeId = t?.id)}
                            getOptionLabel={(option: Option) => option.label}
                            renderInput={(params) => <TextField {...params} placeholder="Valg størrelseskategori"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Kvadratmeterstørrelse på prosjektet (BRA)</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg kvadratmeterstørrelse"
                            disabled={this.state.isLoading}
                            type="number"
                            className="form-control-input"
                            value={this.state.project.realSize}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.realSize = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Gateadresse</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg gateadresse"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.address}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.address = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Postnummer</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg postnummer"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.zip}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.zip = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Sted</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg sted"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.city}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.city = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Fylke</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <Autocomplete
                            options={this.state.regions}
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            getOptionLabel={(option: Option) => option.label}
                            onChange={(e, option) => this.updateState(s => s.project.regionId = option?.id)}
                            renderInput={(params) => <TextField {...params} placeholder="Valg fylke"/>}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Gårdsnummer</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg gårdsnummer"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.gardenNumber}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.gardenNumber = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Bruksnummer</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg bruksnummer"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.useNumber}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.useNumber = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Bygningsnummer</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg bygningsnummer"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.buildingNumber}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.buildingNumber = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Antall bygninger</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg antall bygninger"
                            disabled={this.state.isLoading}
                            type="number"
                            className="form-control-input"
                            value={this.state.project.amountOfBuildings}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.amountOfBuildings = v)}
                            fullWidth
                        />
                    </div>
                </div>
                {this.state.project.buildingTypeId == 283210005 && (
                    <div className="row mb-3">
                        <div className="col-sm-12">
                            <span>Antall boenheter</span>
                        </div>
                        <div className="col-sm-12">
                            <TextField
                                placeholder="Valg antall boenheter"
                                disabled={this.state.isLoading}
                                type="number"
                                className="form-control-input"
                                value={this.state.project.numberOfAppartments}
                                onChange={(event: any) => this.setField(event, (s, v) => s.project.numberOfAppartments = v)}
                                fullWidth
                            />
                        </div>
                    </div>
                )}
                <h4>Kundeinfo</h4>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Kunde/utvikler</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <AsyncPicker
                            getOptions={(name: string) => this.props.getCompaniesByName(name)}
                            getOptionKey={(option: Company) => option.id}
                            getOptionLabel={(option: Company) => option.name}
                            disabled={this.state.isLoading}
                            freeInput
                            placeholder="Valg navn"
                            value={this.state.project.customer}
                            onValueChanged={(option: Company | null | string) => this.setPartOfLookupField(option, "name", (s) => s.project.customer, (s, v) => s.project.customer = v)}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Kundes organisasjonsnummer</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <AsyncPicker
                            getOptions={(name: string) => this.props.getCompaniesByNumber(name)}
                            getOptionKey={(option: Company) => option.id}
                            getOptionLabel={(option: Company) => option.orgNumber}
                            disabled={this.state.isLoading}
                            freeInput
                            placeholder="Valg organisasjonsnummer"
                            value={this.state.project.customer}
                            onValueChanged={(option: Company | null | string) => this.setPartOfLookupField(option, "orgNumber", (s) => s.project.customer, (s, v) => s.project.customer = v)}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Kontaktperson hos kunde/utvikler</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <AsyncPicker
                            getOptions={(name: string) => this.props.getPersonsByName(name)}
                            getOptionKey={(option: Person) => option.id}
                            getOptionLabel={(option: Person) => option.fullName}
                            disabled={this.state.isLoading}
                            freeInput
                            placeholder="Valg navn"
                            value={this.state.project.contactPerson}
                            onValueChanged={(option: Person | null | string) => this.setPartOfLookupField(option, "fullName", (s) => s.project.contactPerson, (s, v) => s.project.contactPerson = v)}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>E-postadresse til kontaktperson</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <AsyncPicker
                            getOptions={(email: string) => this.props.getPersonsByEmail(email)}
                            getOptionKey={(option: Person) => option.id}
                            getOptionLabel={(option: Person) => option.email}
                            disabled={this.state.isLoading}
                            freeInput
                            placeholder="Valg e-post"
                            value={this.state.project.contactPerson}
                            onValueChanged={(option: Person | null | string) => this.setPartOfLookupField(option, "email", (s) => s.project.contactPerson, (s, v) => s.project.contactPerson = v)}
                        />
                    </div>
                </div>
                <h4>Fakturainfo</h4>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Revisors fakturaadresse (e-post eller EHF)</span><span className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg fakturaadresse"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.invoiceEmail}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.invoiceEmail = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Faktura merkes med evt. referanse/ordre/prosjekt-nummer</span><span
                        className="required">*</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg Faktura"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            value={this.state.project.invoiceNumber}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.invoiceNumber = v)}
                            fullWidth
                        />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-sm-12">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={this.state.project.confidential}
                                    onChange={(e, checked) => this.updateState(s => s.project.confidential = checked)}
                                    disabled={this.state.isLoading}
                                    color="primary"
                                />
                            }
                            label="Skal prosjektets dokumentasjon håndteres konfidensielt?(Dette betyr at prosjektet ikke vil være synlig på Grønn Byggallianses nettside over sertifiserte prosjekter)"
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={this.state.project.studyCase}
                                    onChange={(e, checked) => this.updateState(s => s.project.studyCase = checked)}
                                    disabled={this.state.isLoading}
                                    color="primary"
                                />
                            }
                            label="Kan prosjektet brukes som eksempelprosjekt på våre nettsider?"
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <span>Annen relevant informasjon</span>
                    </div>
                    <div className="col-sm-12">
                        <TextField
                            placeholder="Valg annen relevant informasjon"
                            disabled={this.state.isLoading}
                            className="form-control-input"
                            multiline={true}
                            minRows={3}
                            value={this.state.project.comments}
                            onChange={(event: any) => this.setField(event, (s, v) => s.project.comments = v)}
                            fullWidth
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <Stack direction="row" gap={3}>
                            <Button className="mr-3" variant="contained"
                                    disabled={this.state.isLoading || !this.isValid()} color="primary"
                                    onClick={() => this.props.onSave(this.state.project)}>
                                {this.state.isLoading ? (<CircularProgress className="mr-3" size={24}/>) : (
                                    <SaveIcon className="mr-3"/>)}
                                Lagre
                            </Button>
                            <Button variant="contained" disabled={this.state.isLoading}
                                    onClick={() => this.props.onCancel()}>Avbryt</Button>
                        </Stack>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}